/**
 * Tooltip.tsx — PPP Stream C, DLD §C1
 *
 * Thin wrapper around Radix UI Tooltip primitives.
 * Use <Term term="agm">AGM</Term> for domain-abbreviation tooltips.
 * Use <Tooltip content="..."> for ad-hoc button/label tooltips.
 */

import * as RadixTooltip from "@radix-ui/react-tooltip";
import { cn } from "../../lib/cn";

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
}

export function Tooltip({
  content,
  children,
  side = "top",
  className,
}: TooltipProps): JSX.Element {
  return (
    // Per-instance Provider ensures the tooltip works standalone (e.g. in tests
    // or any render tree that doesn't include the root Provider in App.tsx).
    // Nested Radix providers are safe and merge context.
    <RadixTooltip.Provider delayDuration={300}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side={side}
            sideOffset={5}
            className={cn(
              "z-50 max-w-xs rounded-md bg-surface px-2.5 py-1.5",
              "text-xs text-text shadow-md border border-border",
              "animate-in fade-in-0 zoom-in-95",
              className,
            )}
          >
            {content}
            <RadixTooltip.Arrow className="fill-border" />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
